import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Play, Pause, Eye, Search, Shield, TrendingUp, Zap, Users } from 'lucide-react';

/**
 * Product Tour — 4 layout variants sharing the same content.
 * Use <ProductTour variant="scrollytelling" /> to render a specific one.
 * variant ∈ 'storyboard' | 'scrollytelling' | 'tabbed' | 'carousel'
 */

const CHAPTERS_DEFAULT = [
  {
    id: 'see',
    icon: Eye,
    kicker: 'CHAPTER 01 — SEE',
    title: 'Every platform. Every property. Every connection.',
    lede: 'Your entire hospitality tech stack, visualized in one interactive map.',
    body: 'Click any node to see who manages it, what it integrates with, and when the contract renews. Switch between 11 layouts in one click — from category-grouped clusters to strict flowcharts and PMS-centric hubs.',
    shots: [
      { src: '/assets/ss_stackmap_force.jpeg', caption: 'Category layout — all platforms grouped by function' },
      { src: '/assets/ss_stackmap_structured.jpeg', caption: 'Structured layout — clean orthogonal grid' },
      { src: '/assets/ss_stackmap_flowchart.jpeg', caption: 'Flowchart layout — process flow with directional arrows' },
    ],
  },
  {
    id: 'analyze',
    icon: Search,
    kicker: 'CHAPTER 02 — ANALYZE',
    title: '60+ reports. Every question your VP asks — answered.',
    lede: 'Coverage matrix, vendor intelligence, brand compliance, tech debt, cost heatmaps.',
    body: 'Each report is purpose-built for a specific question leadership actually asks. Bulk-fill gaps directly from the coverage matrix. Schedule the executive dashboard to email itself weekly.',
    shots: [
      { src: '/assets/ss_coverage_new.jpeg', caption: 'Coverage Matrix — spot every gap in your portfolio' },
      { src: '/assets/ss_executive.jpeg', caption: 'Executive Overview — the numbers that matter' },
      { src: '/assets/ss_vendor_deployment.jpeg', caption: 'Vendor Deployment — know your vendors better than they know you' },
    ],
  },
  {
    id: 'ai',
    icon: Zap,
    kicker: 'CHAPTER 03 — ACCELERATE',
    title: '14 AI assistants that understand enterprise hospitality tech.',
    lede: 'From integration mapping to RFP writing — AI does the heavy lifting.',
    body: 'Paste contract text, get structured terms. Describe a new property, get a recommended stack. Ask a plain-English question, get a real report. All grounded in your actual data.',
    shots: [
      { src: '/assets/ss_ai_hub.jpeg', caption: '14 assistant tiles in the AI Hub' },
      { src: '/assets/ss_wizard_new.jpeg', caption: 'AI onboarding wizard — build a stack in 5 steps' },
      { src: '/assets/ss_tech_database.jpeg', caption: '900+ products, AI-enriched, pre-categorized' },
    ],
  },
  {
    id: 'govern',
    icon: Shield,
    kicker: 'CHAPTER 04 — GOVERN',
    title: 'Brand standards met. Audits passed. Sleep restored.',
    lede: 'Enterprise frameworks: SOC 2 · GDPR · PCI DSS · HIPAA · ISO 27001.',
    body: '5-dimension governance across every assignment — Admin, Credentials, Daily, Billing, Support. Granular RBAC with role preview. Full audit trail. White-label for tenant portfolios.',
    shots: [
      { src: '/assets/ss_governance_new.jpeg', caption: 'Governance Matrix — who owns what, everywhere' },
      { src: '/assets/ss_compliance.jpeg', caption: 'Brand Compliance — Required / Approved / Not Allowed' },
      { src: '/assets/ss_roles.jpeg', caption: 'Granular RBAC — control every action per role' },
    ],
  },
  {
    id: 'operate',
    icon: TrendingUp,
    kicker: 'CHAPTER 05 — OPERATE',
    title: 'Rollouts tracked. Status known. Zero surprises.',
    lede: 'Change management, system status, onboarding checklists, SLA tracking.',
    body: 'Every rollout gets a per-property status board. Every platform gets a real-time health check. Every new opening gets an auto-generated setup checklist. Vendor SLAs tracked and scored.',
    shots: [
      { src: '/assets/ss_change_mgmt.jpeg', caption: 'Change Management — track every rollout' },
      { src: '/assets/ss_system_status.jpeg', caption: 'System Status — traffic-light view per platform' },
      { src: '/assets/ss_checklist.jpeg', caption: 'Onboarding Checklists — never miss a step' },
    ],
  },
  {
    id: 'scale',
    icon: Users,
    kicker: 'CHAPTER 06 — SCALE',
    title: 'From five properties to five hundred.',
    lede: 'Multi-tenant architecture. Property cloning. Bulk import. Mobile-first PWA.',
    body: 'Clone your best-configured property to a new opening in one click. Import legacy spreadsheets in a 3-step wizard. Bottom-nav mobile experience in 4 languages. Everything scales without adding headcount.',
    shots: [
      { src: '/assets/ss_home_dashboard.jpeg', caption: 'Portfolio dashboard — every property at a glance' },
      { src: '/assets/ss_admin_console.jpeg', caption: 'Multi-tenant admin console' },
      { src: '/assets/ss_support_mobile.jpeg', caption: 'Mobile-first PWA' },
    ],
  },
];

const PURPLE = '#352272';
const PURPLE_LIGHT = '#F5F3FA';

// Chapters context — allows the CMS-driven /preview to inject different
// chapters (title/kicker/body/screenshots) at runtime without touching the
// visual layout code below.
const ICON_MAP = { Eye, Search, Zap, Shield, TrendingUp, Users, Play };
function resolveChapter(ch) {
  if (!ch) return ch;
  if (typeof ch.icon === 'string') {
    return { ...ch, icon: ICON_MAP[ch.icon] || Eye };
  }
  return ch;
}
const ChaptersContext = React.createContext(CHAPTERS_DEFAULT);
function useChapters() {
  const raw = React.useContext(ChaptersContext);
  return React.useMemo(() => (Array.isArray(raw) && raw.length ? raw.map(resolveChapter) : CHAPTERS_DEFAULT), [raw]);
}

// Browser-chrome mockup wrapper (matches PreviewSite look)
function BrowserChrome({ src, alt, className = '' }) {
  return (
    <div className={`border border-[#E5E5E5] overflow-hidden bg-white shadow-2xl shadow-black/10 ${className}`}>
      <div className="h-2 bg-[#352272] flex items-center px-2 gap-1">
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
      </div>
      <img src={src} alt={alt} className="w-full block" loading="lazy" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// A1 — HORIZONTAL STORYBOARD
// ─────────────────────────────────────────────────────────────
function TourStoryboard() {
  const CHAPTERS = useChapters();
  const scrollerRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const scrollTo = (i) => {
    const el = scrollerRef.current?.children?.[i];
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    setIdx(i);
  };

  return (
    <section className="py-20 sm:py-28 bg-[#FAFAFA]" data-testid="tour-storyboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-10">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#352272]/50 mb-3">The Product Tour</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Six chapters. Six problems solved.
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => scrollTo(Math.max(0, idx - 1))}
              className="h-9 w-9 border border-[#E5E5E5] flex items-center justify-center hover:bg-white transition-colors"
              data-testid="storyboard-prev"
            ><ArrowLeft className="h-4 w-4" /></button>
            <button
              onClick={() => scrollTo(Math.min(CHAPTERS.length - 1, idx + 1))}
              className="h-9 w-9 border border-[#E5E5E5] flex items-center justify-center hover:bg-white transition-colors"
              data-testid="storyboard-next"
            ><ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-8"
        style={{ scrollbarWidth: 'thin', paddingLeft: 'max(calc((100vw - 1280px) / 2), 24px)', paddingRight: 'max(calc((100vw - 1280px) / 2), 24px)' }}
      >
        {CHAPTERS.map((ch, i) => {
          const Icon = ch.icon;
          return (
            <div
              key={ch.id}
              className="snap-center shrink-0 w-[85vw] max-w-3xl bg-white border border-[#E5E5E5] p-8 lg:p-10"
              data-testid={`storyboard-chapter-${ch.id}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 flex items-center justify-center" style={{ background: PURPLE_LIGHT }}>
                  <Icon className="h-5 w-5" style={{ color: PURPLE }} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: PURPLE }}>{ch.kicker}</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{ch.title}</h3>
              <p className="text-base text-[#374151] mb-6">{ch.lede}</p>
              <BrowserChrome src={ch.shots[0].src} alt={ch.shots[0].caption} className="mb-3" />
              <p className="text-xs text-[#374151]/70 italic mb-6">{ch.shots[0].caption}</p>
              <p className="text-sm text-[#374151] leading-relaxed">{ch.body}</p>
              <div className="mt-6 flex gap-2 text-[10px] text-[#374151]/60">
                Chapter {i + 1} of {CHAPTERS.length}
              </div>
            </div>
          );
        })}
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-6 flex items-center justify-center gap-2">
        {CHAPTERS.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === idx ? 24 : 6,
              background: i === idx ? PURPLE : `${PURPLE}30`,
            }}
            aria-label={`Go to chapter ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// A2 — VERTICAL SCROLLYTELLING
// ─────────────────────────────────────────────────────────────
function TourScrollytelling() {
  const CHAPTERS = useChapters();
  return (
    <section className="bg-white" data-testid="tour-scrollytelling">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-8">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#352272]/50 mb-3">The Product Tour</p>
        <h2 className="text-3xl sm:text-5xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Scroll to see LodgiFlow in action.
        </h2>
        <p className="mt-4 text-lg text-[#374151] max-w-xl">Six chapters. Every screen is real.</p>
      </div>

      {CHAPTERS.map((ch, i) => (
        <ScrollyChapter key={ch.id} chapter={ch} index={i} />
      ))}
    </section>
  );
}

function ScrollyChapter({ chapter, index }) {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });
  const shotIdx = useTransform(scrollYProgress, [0, 0.4, 0.7, 1], [0, 0, 1, 2]);
  const [currentShot, setCurrentShot] = useState(0);

  useEffect(() => {
    const unsubscribe = shotIdx.on('change', (v) => {
      setCurrentShot(Math.min(chapter.shots.length - 1, Math.round(v)));
    });
    return () => unsubscribe();
  }, [shotIdx, chapter.shots.length]);

  const Icon = chapter.icon;
  return (
    <div
      ref={containerRef}
      className={`relative ${index % 2 === 0 ? 'bg-white' : 'bg-[#F5F3FA]'}`}
      style={{ minHeight: '220vh' }}
      data-testid={`scrolly-chapter-${chapter.id}`}
    >
      <div className="sticky top-0 h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ background: PURPLE }}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: PURPLE }}>{chapter.kicker}</span>
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {chapter.title}
            </h3>
            <p className="text-lg text-[#374151] mb-6">{chapter.lede}</p>
            <p className="text-base text-[#374151] leading-relaxed">{chapter.body}</p>

            {/* Progress dots for shots */}
            <div className="mt-8 flex items-center gap-3">
              {chapter.shots.map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-1.5 rounded-full transition-all" style={{ width: i === currentShot ? 32 : 8, background: i === currentShot ? PURPLE : `${PURPLE}30` }} />
                </div>
              ))}
              <span className="text-xs text-[#374151]/70 ml-2 italic">{chapter.shots[currentShot]?.caption}</span>
            </div>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentShot}
                initial={{ opacity: 0, scale: 0.98, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98, y: -12 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                <BrowserChrome src={chapter.shots[currentShot].src} alt={chapter.shots[currentShot].caption} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// A3 — TABBED / STEPPED (compact, high-density, page-filling)
// ─────────────────────────────────────────────────────────────
function TourTabbed() {
  const CHAPTERS = useChapters();
  const [activeIdx, setActiveIdx] = useState(0);
  const [shotIdx, setShotIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 within current shot's dwell
  const active = CHAPTERS[activeIdx];
  const Icon = active.icon;
  const DWELL_MS = 8000;

  useEffect(() => { setShotIdx(0); }, [activeIdx]);

  // Keyboard navigation — only active when tour section is in viewport
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!sectionRef.current) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    io.observe(sectionRef.current);
    return () => io.disconnect();
  }, []);

  // Any manual interaction resets progress timer
  const resetProgress = () => setProgress(0);

  useEffect(() => {
    if (!inView) return;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(CHAPTERS.length - 1, i + 1)); resetProgress(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); resetProgress(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setShotIdx(i => Math.min(active.shots.length - 1, i + 1)); resetProgress(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setShotIdx(i => Math.max(0, i - 1)); resetProgress(); }
      else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inView, active.shots.length]);

  // Auto-advance loop — 8 seconds per shot, then advance to next shot (or next chapter)
  useEffect(() => {
    if (!inView || paused) { setProgress(0); return; }
    const start = Date.now();
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(1, elapsed / DWELL_MS);
      setProgress(p);
      if (p >= 1) {
        // Advance to next shot; wrap to next chapter when shots exhausted
        if (shotIdx < active.shots.length - 1) {
          setShotIdx(shotIdx + 1);
        } else if (activeIdx < CHAPTERS.length - 1) {
          setActiveIdx(activeIdx + 1);
        } else {
          // Reached the end — pause auto-advance
          setPaused(true);
        }
        setProgress(0);
      }
    }, 60);
    return () => clearInterval(iv);
  }, [inView, paused, shotIdx, activeIdx, active.shots.length]);

  return (
    <section
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F3FA 50%, #FAFAFA 100%)' }}
      data-testid="tour-tabbed"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Decorative gradient orbs */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 15% 20%, #352272 0%, transparent 40%), radial-gradient(circle at 85% 80%, #6B52AE 0%, transparent 40%)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section header */}
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#352272]/60 mb-3">The Product Tour · Six chapters</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Explore the platform at <span className="italic text-[#352272]">your own pace</span>.
          </h2>
          {/* Keyboard hint — desktop only */}
          <div className="hidden lg:inline-flex items-center gap-2 mt-6 px-3 py-1.5 rounded-full bg-white/60 border border-[#E5E5E5] text-[10px] uppercase tracking-widest text-[#374151]/70" data-testid="tour-keyboard-hint">
            <kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded border border-[#D4D4D4] bg-white shadow-[0_1px_0_#D4D4D4] font-mono text-[10px] text-[#0F172A]">↑</kbd>
            <kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded border border-[#D4D4D4] bg-white shadow-[0_1px_0_#D4D4D4] font-mono text-[10px] text-[#0F172A]">↓</kbd>
            <span className="text-[10px] normal-case tracking-normal">chapters</span>
            <span className="text-[#374151]/30">·</span>
            <kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded border border-[#D4D4D4] bg-white shadow-[0_1px_0_#D4D4D4] font-mono text-[10px] text-[#0F172A]">←</kbd>
            <kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded border border-[#D4D4D4] bg-white shadow-[0_1px_0_#D4D4D4] font-mono text-[10px] text-[#0F172A]">→</kbd>
            <span className="text-[10px] normal-case tracking-normal">shots</span>
            <span className="text-[#374151]/30">·</span>
            <kbd className="inline-flex items-center justify-center h-5 px-2 rounded border border-[#D4D4D4] bg-white shadow-[0_1px_0_#D4D4D4] font-mono text-[10px] text-[#0F172A]">space</kbd>
            <span className="text-[10px] normal-case tracking-normal">{paused ? 'play' : 'pause'}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8 lg:gap-12 items-start">
          {/* Vertical chapter list (left rail) */}
          <div className="lg:sticky lg:top-24 space-y-2">
            {CHAPTERS.map((ch, i) => {
              const isActive = i === activeIdx;
              const CIcon = ch.icon;
              return (
                <button
                  key={ch.id}
                  onClick={() => { setActiveIdx(i); resetProgress(); }}
                  data-testid={`tabbed-tab-${ch.id}`}
                  className={`relative w-full text-left px-4 py-3.5 rounded-xl transition-all group flex items-start gap-3 overflow-hidden ${
                    isActive
                      ? 'bg-white shadow-lg shadow-[#352272]/10 border border-[#352272]/20'
                      : 'border border-transparent hover:bg-white/60 hover:border-[#E5E5E5]'
                  }`}
                >
                  {/* Auto-advance progress bar on active card */}
                  {isActive && !paused && (
                    <div
                      className="absolute bottom-0 left-0 h-0.5 bg-[#352272]/70 transition-none"
                      style={{ width: `${progress * 100}%` }}
                      data-testid="tour-progress"
                    />
                  )}
                  <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-[#352272] text-white' : 'bg-[#F5F3FA] text-[#352272] group-hover:bg-[#EBE5F7]'}`}>
                    <CIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${isActive ? 'text-[#352272]' : 'text-[#374151]/60'}`}>
                      Chapter {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className={`text-sm font-semibold mt-0.5 ${isActive ? 'text-[#0F172A]' : 'text-[#374151] group-hover:text-[#0F172A]'}`}>
                      {ch.kicker.split('— ')[1]?.charAt(0) + ch.kicker.split('— ')[1]?.slice(1).toLowerCase()}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Chapter content (right — hero + text + thumbs) */}
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="min-w-0"
          >
            {/* Chapter header */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#352272] text-white shadow-md shadow-[#352272]/25">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#352272]">{active.kicker}</div>
                  <div className="text-[11px] text-[#374151]/60">Chapter {activeIdx + 1} of {CHAPTERS.length}</div>
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{active.title}</h3>
              <p className="text-base sm:text-lg text-[#374151] leading-relaxed max-w-3xl">{active.lede}</p>
            </div>

            {/* Big hero screenshot with cinematic parallax on transition */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${active.id}-${shotIdx}`}
                initial={{ opacity: 0, x: 40, scale: 0.985, filter: 'blur(4px)' }}
                animate={{ opacity: 1, x: 0, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, x: -24, scale: 0.99, filter: 'blur(2px)' }}
                transition={{
                  duration: 0.55,
                  ease: [0.16, 1, 0.3, 1],  // Apple-keynote-style cubic-bezier
                  filter: { duration: 0.35 },
                }}
              >
                <BrowserChrome src={active.shots[shotIdx].src} alt={active.shots[shotIdx].caption} />
                <motion.p
                  className="mt-3 text-xs text-[#374151]/70 italic text-center"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  {active.shots[shotIdx].caption}
                </motion.p>
              </motion.div>
            </AnimatePresence>

            {/* Thumbnail selector + body copy */}
            <div className="mt-6 grid grid-cols-[auto_1fr] gap-6 items-start">
              <div className="flex gap-2 shrink-0">
                {active.shots.map((s, i) => (
                  <button
                    key={s.src}
                    onClick={() => { setShotIdx(i); resetProgress(); }}
                    data-testid={`tabbed-thumb-${active.id}-${i}`}
                    className={`w-20 border-2 rounded transition-all overflow-hidden ${i === shotIdx ? 'border-[#352272] opacity-100' : 'border-transparent opacity-50 hover:opacity-100'}`}
                    aria-label={s.caption}
                  >
                    <img src={s.src} alt={s.caption} className="w-full h-auto block" loading="lazy" />
                  </button>
                ))}
              </div>
              <p className="text-sm text-[#374151] leading-relaxed">{active.body}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// A4 — AUTO-PLAY CAROUSEL
// ─────────────────────────────────────────────────────────────
function TourCarousel() {
  const CHAPTERS = useChapters();
  // Flatten all shots into slides
  const slides = CHAPTERS.flatMap((ch) => ch.shots.map((s) => ({ ...s, chapter: ch })));
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!playing || hovered) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [playing, hovered, slides.length]);

  const current = slides[idx];
  const Icon = current.chapter.icon;

  return (
    <section className="py-20 sm:py-28 bg-[#352272] text-white overflow-hidden relative" data-testid="tour-carousel">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #7C3AED 0%, transparent 50%), radial-gradient(circle at 80% 70%, #4F46E5 0%, transparent 50%)' }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-white/40 mb-3">The Product Tour</p>
        <div className="flex items-end justify-between mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The whole platform in {slides.length} slides.
          </h2>
          <button
            onClick={() => setPlaying(!playing)}
            data-testid="carousel-playpause"
            className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:bg-white/10 transition-colors text-xs"
          >
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {playing ? 'Pause' : 'Play'}
          </button>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-10 items-center">
            <div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/60">{current.chapter.kicker}</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{current.chapter.title}</h3>
                  <p className="text-sm text-white/70 mb-4">{current.chapter.lede}</p>
                  <p className="text-sm text-white/80 italic border-l-2 border-white/30 pl-4">{current.caption}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.03 }}
                  transition={{ duration: 0.5 }}
                >
                  <BrowserChrome src={current.src} alt={current.caption} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-8 flex items-center gap-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`carousel-dot-${i}`}
                className="flex-1 h-0.5 relative overflow-hidden bg-white/15"
                aria-label={`Go to slide ${i + 1}`}
              >
                {i === idx && playing && !hovered && (
                  <motion.div
                    className="absolute inset-0 bg-white"
                    initial={{ scaleX: 0, transformOrigin: 'left' }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 5, ease: 'linear' }}
                  />
                )}
                {i < idx && <div className="absolute inset-0 bg-white/60" />}
                {i === idx && (!playing || hovered) && <div className="absolute inset-0 bg-white/60" />}
              </button>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-white/50 tabular-nums">{idx + 1} / {slides.length}</div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Public API — variant switch
// ─────────────────────────────────────────────────────────────
export default function ProductTour({ variant = 'tabbed', chapters }) {
  const inner = (() => {
    switch (variant) {
      case 'storyboard': return <TourStoryboard />;
      case 'tabbed': return <TourTabbed />;
      case 'carousel': return <TourCarousel />;
      case 'scrollytelling':
      default: return <TourScrollytelling />;
    }
  })();
  return (
    <ChaptersContext.Provider value={Array.isArray(chapters) && chapters.length ? chapters : CHAPTERS_DEFAULT}>
      {inner}
    </ChaptersContext.Provider>
  );
}

export { TourStoryboard, TourScrollytelling, TourTabbed, TourCarousel };
